import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  userType: 'primary' | 'middle' | 'high' | 'teacher' | 'admin';
}

export default function LoginModal({ isOpen, onClose, userType }: LoginModalProps) {
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const userTypeConfig = {
    primary: {
      title: '小学生登录',
      icon: 'ri-user-smile-line',
      color: 'from-pink-500 to-purple-500',
      redirectPath: '/primary'
    },
    middle: {
      title: '初中生登录',
      icon: 'ri-user-line',
      color: 'from-blue-500 to-cyan-500',
      redirectPath: '/middle'
    },
    high: {
      title: '高中生登录',
      icon: 'ri-user-star-line',
      color: 'from-purple-500 to-indigo-500',
      redirectPath: '/high'
    },
    teacher: {
      title: '教师登录',
      icon: 'ri-user-settings-line',
      color: 'from-green-500 to-emerald-500',
      redirectPath: '/teacher'
    },
    admin: {
      title: '管理员登录',
      icon: 'ri-admin-line',
      color: 'from-indigo-500 to-blue-500',
      redirectPath: '/admin'
    }
  };

  const config = userTypeConfig[userType];

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    // 模拟登录成功后跳转
    onClose();
    navigate(config.redirectPath);
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl max-w-md w-full p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl text-gray-600"></i>
        </button>

        <div className="text-center mb-8">
          <div className={`w-20 h-20 bg-gradient-to-br ${config.color} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
            <i className={`${config.icon} text-4xl text-white`}></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">{config.title}</h2>
          <p className="text-gray-600 mt-2">请输入您的账号信息</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              账号
            </label>
            <input
              type="text"
              placeholder="请输入账号"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              密码
            </label>
            <input
              type="password"
              placeholder="请输入密码"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm"
              required
            />
          </div>

          <div className="flex items-center justify-between text-sm">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input type="checkbox" className="w-4 h-4 text-blue-600 rounded cursor-pointer" />
              <span className="text-gray-600 whitespace-nowrap">记住我</span>
            </label>
            <a href="#" className="text-blue-600 hover:text-blue-700 whitespace-nowrap">忘记密码？</a>
          </div>

          <button
            type="submit"
            className={`w-full py-3 bg-gradient-to-r ${config.color} text-white rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer`}
          >
            登录
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-600">
          还没有账号？
          <a href="#" className="text-blue-600 hover:text-blue-700 ml-1 whitespace-nowrap">立即注册</a>
        </div>
      </div>
    </div>
  );
}
